import { useEffect, useMemo, useState } from 'react';

import { loadBakedCatalog } from '@/lib/catalog/load-catalog';
import { filterByEntitlements } from '@/lib/entitlements';
import type { CatalogApp } from '@/types/catalog';

type CatalogState = {
  apps: CatalogApp[];
  loading: boolean;
  error: string | null;
};

/**
 * Loads the CI-baked SSM catalog (`/app-catalog.json`), expands Keycloak
 * admin consoles into environment tiles, appends hard-coded Google Workspace
 * and Exchange tiles, then filters by the signed-in user's Keycloak `groups`.
 */
export const useCatalog = (groups: string[], email = ''): CatalogState => {
  const [catalog, setCatalog] = useState<CatalogApp[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const apps = await loadBakedCatalog(controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setCatalog(apps);
        setLoading(false);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setCatalog(null);
        setError(err instanceof Error ? err.message : 'Failed to load catalog');
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  const apps = useMemo(() => filterByEntitlements(catalog ?? [], groups, email), [catalog, groups, email]);

  return { apps, loading, error };
};
